import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';

import useApi from 'common/useApi';
import httpClient from 'common/useApi/httpClient';
import UiContext from 'common/UiContext/UiContext';
import INotification from 'common/types/INotification';

import DataTablePagination from 'components/DataTablePagination';

import './Notifications.scss';

const humanize = (value: string) =>
  value.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

// Full, paginated history of the account's notifications — the bell dropdown
// only shows the latest page, this is the "see all" destination.
const Notifications = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useContext(UiContext);
  const [page, setPage] = useState(1);

  const { data, isError, isLoading, isFetching } = useApi(
    'getList',
    '/api/notifications',
    { page },
  );

  useEffect(() => {
    if (isError) addToast!('Error loading notifications');
  }, [isError]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
    queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
  };

  const markAllRead = () => {
    httpClient.post('/api/notifications/mark-all-read/').then(invalidate);
  };

  const openNotification = (notification: INotification) => {
    const finish = () => {
      invalidate();
      if (notification.link) navigate(`/${notification.link.replace(/^\//, '')}`);
    };

    if (notification.read_at) {
      finish();
      return;
    }
    httpClient
      .post(`/api/notifications/${notification.id}/mark-read/`)
      .then(finish)
      .catch(finish);
  };

  const toggleRead = (notification: INotification) => {
    const endpoint = notification.read_at ? 'mark-unread' : 'mark-read';

    httpClient.post(`/api/notifications/${notification.id}/${endpoint}/`).then(invalidate);
  };

  const dismiss = (notification: INotification) => {
    httpClient.delete(`/api/notifications/${notification.id}/`).then(invalidate);
  };

  const notifications: INotification[] = data?.results ?? [];
  const hasUnread = notifications.some((n) => !n.read_at);

  if (!data) {
    return null;
  }

  return (
    <div className={classNames({ box: true, isLoading: isLoading || isFetching, 'notifications-page': true })}>
      <div className='notifications-toolbar'>
        <span>{data?.count ?? 0} total</span>
        {hasUnread && (
          <button type='button' className='mark-all' onClick={markAllRead}>
            Mark all read
          </button>
        )}
      </div>
      <div className='notifications-list'>
        {notifications.length === 0 && (
          <div className='notifications-empty'>You have no notifications yet.</div>
        )}
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={classNames({
              'notifications-item': true,
              unread: !notification.read_at,
              [notification.level]: true,
            })}
          >
            <button
              type='button'
              className='notifications-item-open'
              onClick={() => openNotification(notification)}
            >
              <div className='notifications-item-main'>
                <span className='notifications-item-type'>{humanize(notification.category)}</span>
                <span className='notifications-item-title'>{notification.title}</span>
                {notification.body && (
                  <span className='notifications-item-body'>{notification.body}</span>
                )}
              </div>
              <div className='notifications-item-meta'>
                {notification.subaccount_username && (
                  <span className='notifications-chip'>{notification.subaccount_username}</span>
                )}
                <span className='notifications-item-time'>
                  {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                </span>
              </div>
            </button>
            <div className='notifications-item-actions'>
              <button
                type='button'
                className='notifications-action'
                onClick={() => toggleRead(notification)}
              >
                {notification.read_at ? 'Mark unread' : 'Mark read'}
              </button>
              <button
                type='button'
                className='notifications-action dismiss'
                aria-label='Dismiss notification'
                onClick={() => dismiss(notification)}
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>
      <DataTablePagination
        page={page}
        totalItems={data?.count ?? 0}
        setPage={setPage}
      />
    </div>
  );
};

export default Notifications;
